import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Button,
  Badge,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Select,
  Link,
} from "@chakra-ui/react";
import { FiDownload, FiExternalLink, FiCalendar, FiDollarSign, FiCreditCard, FiSearch, FiMail } from "react-icons/fi";
import { useGetUserBilling, useGetUserPayments, useGetBillingPortal } from "../../services/settingsService";

const BillingSection = () => {
  const toast = useToast();

  // State for payment history filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch billing data
  const { billingData, billingIsLoading, billingError } = useGetUserBilling();
  const { paymentsData, paymentsIsLoading, paymentsError } = useGetUserPayments();
  const { billingPortalPayload, billingPortalIsLoading } = useGetBillingPortal();

  const handleBillingPortal = async () => {
    try {
      const result = await billingPortalPayload();
      if (result?.url) {
        window.open(result.url, '_blank');
      } else {
        toast({
          title: "Billing Portal",
          description: "Billing portal URL not available. Please contact support.",
          status: "info",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open billing portal. Please try again or contact support.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    // Handle null, undefined, or invalid amounts
    if (!amount) {
      return 'N/A';
    }
    
    // If amount is already a formatted string (like "$49.00"), return it as is
    if (typeof amount === 'string' && amount.includes('$')) {
      return amount;
    }
    
    // Convert to number if it's a string
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Check if the amount is already in dollars (not cents)
    // If amount is less than 1000, assume it's already in dollars
    const finalAmount = numericAmount < 1000 ? numericAmount : numericAmount / 100;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(finalAmount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Filter and search payments
  const filteredPayments = useMemo(() => {
    if (!paymentsData || !Array.isArray(paymentsData)) return [];
    
    return paymentsData.filter(payment => {
      const matchesSearch = searchTerm === "" || 
        payment.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.type?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || payment.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [paymentsData, searchTerm, statusFilter]);

  if (billingIsLoading || paymentsIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color="gray.600">Loading billing information...</Text>
      </Box>
    );
  }

  if (billingError || paymentsError) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="red.500">Failed to load billing information</Text>
        <Button mt={4} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" id="billing-section">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Billing & Payments
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Manage your billing information and payment history
          </Text>
        </VStack>
        <Button
          leftIcon={<FiExternalLink />}
          colorScheme="blue"
          onClick={handleBillingPortal}
          isLoading={billingPortalIsLoading}
        >
          Billing Portal
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Billing Summary */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="gray.900">
                Billing Summary
              </Heading>

              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Customer</Text>
                  <Text fontWeight="500">{billingData?.user?.name || 'N/A'}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Email</Text>
                  <Text fontWeight="500">{billingData?.user?.email || 'N/A'}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Company</Text>
                  <Text fontWeight="500">{billingData?.user?.company || 'N/A'}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Customer ID</Text>
                  <Text fontSize="sm" fontFamily="mono" color="gray.500">
                    {billingData?.stripe_customer_id || 'N/A'}
                  </Text>
                </HStack>
              </VStack>

              <Divider />

              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Current Plan</Text>
                  <Badge colorScheme="blue" variant="subtle">
                    {billingData?.subscription?.plan || 'N/A'}
                  </Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Billing Cycle</Text>
                  <Text fontWeight="500">
                    {billingData?.subscription?.current_period_start && 
                     billingData?.subscription?.current_period_end
                      ? `${formatDate(billingData.subscription.current_period_start)} - ${formatDate(billingData.subscription.current_period_end)}`
                      : 'N/A'
                    }
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Next Payment</Text>
                  <Text fontWeight="500">
                    {billingData?.subscription?.next_payment 
                      ? formatDate(billingData.subscription.next_payment)
                      : 'N/A'
                    }
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Amount</Text>
                  <Text fontWeight="500">
                    {billingData?.subscription?.amount_cents 
                      ? formatCurrency(billingData.subscription.amount_cents, billingData.subscription.currency)
                      : billingData?.subscription?.amount 
                        ? formatCurrency(billingData.subscription.amount, billingData.subscription.currency)
                        : 'N/A'
                    }
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Payment Methods */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="gray.900">
                Payment Methods
              </Heading>

              {billingData?.payment_methods?.length > 0 ? (
                <VStack spacing={3} align="stretch">
                  {billingData.payment_methods.map((method, index) => (
                    <Box
                      key={method.id}
                      p={4}
                      border="1px solid"
                      borderColor={method.is_default ? "blue.200" : "gray.200"}
                      borderRadius="md"
                      bg={method.is_default ? "blue.50" : "white"}
                    >
                      <HStack justify="space-between" align="center">
                        <HStack spacing={3}>
                          <FiCreditCard size={20} color="gray.400" />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="500">
                              {method.card?.brand?.toUpperCase()} •••• {method.card?.last4}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              Expires {method.card?.exp_month}/{method.card?.exp_year}
                            </Text>
                          </VStack>
                        </HStack>
                        {method.is_default && (
                          <Badge colorScheme="blue" variant="subtle" size="sm">
                            Default
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Box textAlign="center" py={6}>
                  <FiCreditCard size={48} color="gray.300" />
                  <Text mt={2} color="gray.600">No payment methods found</Text>
                </Box>
              )}

              <Button variant="outline" leftIcon={<FiCreditCard />}>
                Add Payment Method
              </Button>
              
              <Divider />
              
              <VStack spacing={1} align="start">
                <Text fontSize="xs" color="gray.500" fontWeight="500">
                  Need Help?
                </Text>
                <Link 
                  href="mailto:support@weetrak.org" 
                  color="blue.500" 
                  fontSize="sm"
                  _hover={{ textDecoration: "underline" }}
                >
                  support@weetrak.org
                </Link>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Payment History */}
      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between" align="center">
              <Heading size="md" color="gray.900">
                Payment History
              </Heading>
              <Button
                leftIcon={<FiDownload />}
                variant="outline"
                size="sm"
              >
                Export
              </Button>
            </HStack>

            {/* Search and Filter Controls */}
            <Flex gap={4} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search payments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="white"
                />
              </InputGroup>
              
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                maxW="200px"
                bg="white"
              >
                <option value="all">All Status</option>
                <option value="succeeded">Succeeded</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="canceled">Canceled</option>
              </Select>
            </Flex>

            {/* Results Count */}
            <Text fontSize="sm" color="gray.600">
              Showing {filteredPayments.length} of {paymentsData?.length || 0} payments
            </Text>

            {filteredPayments.length > 0 ? (
              <Box
                maxH="400px"
                overflowY="auto"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                      <Tr>
                        <Th>Date</Th>
                        <Th>Description</Th>
                        <Th>Amount</Th>
                        <Th>Status</Th>
                        <Th>Type</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredPayments.map((payment) => (
                        <Tr key={payment.id} _hover={{ bg: "gray.50" }}>
                          <Td fontSize="sm">{formatDate(payment.created)}</Td>
                          <Td fontSize="sm" maxW="200px" isTruncated>
                            {payment.description}
                          </Td>
                          <Td fontWeight="500" fontSize="sm">
                            {formatCurrency(payment.amount, payment.currency)}
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={
                                payment.status === 'succeeded' ? 'green' : 
                                payment.status === 'pending' ? 'yellow' :
                                payment.status === 'failed' ? 'red' : 'gray'
                              }
                              variant="subtle"
                              size="sm"
                            >
                              {payment.status}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge variant="outline" size="sm">
                              {payment.type}
                            </Badge>
                          </Td>
                          <Td>
                            <HStack spacing={1}>
                              {payment.receipt_url && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  leftIcon={<FiExternalLink />}
                                  onClick={() => window.open(payment.receipt_url, '_blank')}
                                >
                                  Receipt
                                </Button>
                              )}
                              {payment.invoice_pdf && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  leftIcon={<FiDownload />}
                                  onClick={() => window.open(payment.invoice_pdf, '_blank')}
                                >
                                  Invoice
                                </Button>
                              )}
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Box textAlign="center" py={8}>
                <FiCalendar size={48} color="gray.300" />
                <Text mt={2} color="gray.600">
                  {paymentsData?.length > 0 
                    ? "No payments match your search criteria" 
                    : "No payment history found"
                  }
                </Text>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default BillingSection; 